import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text as ReactText,
  View,
} from "react-native";
import { IconButton, List, RadioButton, Text } from "react-native-paper";
import { functions } from "../../firebase";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { logOutAction, setLanguageAction } from "../../redux/slices/authSlice";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { ThemeToggleSwitch } from "../../components/ThemeToggleSwitch";
import LogoutButton from "../../components/LogoutButton";
import { update } from "../../services/firestoreServices";
import { USERS_COLLECTION } from "../../constants/collections";
import UpdateNotification from "../../components/UpdateNotification";
import { SafeAreaView } from "react-native-safe-area-context";
import UserAvatar from "../../components/UserAvatar";

interface SettingsScreenProps {
  navigation: any;
}

export default function SettingsScreen({ navigation }: SettingsScreenProps) {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);
  const language = useAppSelector((state) => state.auth.language);
  const [clickCount, setClickCount] = useState(0);
  const role = user.role;
  const dispatch = useAppDispatch();

  const deleteUser = async () => {
    try {
      if (clickCount >= 5) {
        await functions().httpsCallable("deleteUser")({ role });
        dispatch(logOutAction());
      }
      setClickCount(clickCount + 1);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.contentContainer}>
        <List.Section>
          <List.Subheader>{t("Appearance")}</List.Subheader>
          <List.Item
            title={t("Theme")}
            description={t("Switch between light and dark mode")}
            left={(props) => <List.Icon {...props} icon="theme-light-dark" />}
            right={() => <ThemeToggleSwitch />}
          />
          <List.Item
            title={t("Language")}
            description={t("Select your preferred language")}
            left={(props) => <List.Icon {...props} icon="translate" />}
          />
          <View style={styles.languageContainer}>
            <RadioButton.Group
              onValueChange={async (value) => {
                if (value === "ru" || value === "kk") {
                  try {
                    await update(USERS_COLLECTION, user.uid, {
                      language: value,
                    });
                    dispatch(setLanguageAction(value));
                  } catch (error) {
                    console.error("Error updating language:", error);
                  }
                }
              }}
              value={language}
            >
              <View style={styles.radioItem}>
                <RadioButton value="ru" />
                <Text>Русский</Text>
              </View>
              <View style={styles.radioItem}>
                <RadioButton value="kk" />
                <Text>Қазақша</Text>
              </View>
            </RadioButton.Group>
          </View>
        </List.Section>

        <View style={{ marginVertical: 10 }}>
          <List.Section>
            <List.Subheader>{t("Profile Information")}</List.Subheader>
            <List.Item
              title={t("Avatar")}
              description={t("Edit Profile")}
              left={() => (
                <UserAvatar
                  size={40}
                  avatarUrl={user.avatarUrl}
                  fullName={user.fullName}
                  email={user.email}
                />
              )}
              onPress={() => navigation.navigate("EditProfile")}
            />
            <List.Item
              title="Email"
              description={user.email}
              left={(props) => <List.Icon {...props} icon="email" />}
            />
            <List.Item
              title={t("User role")}
              description={t(role)}
              left={(props) => <List.Icon {...props} icon="account" />}
            />
            <List.Item
              title={t("Company")}
              description={user.company?.name}
              left={(props) => <List.Icon {...props} icon="office-building" />}
            />
            <List.Item
              title={t("Full name")}
              description={`${user.firstName} ${user.lastName}`}
              left={(props) => <List.Icon {...props} icon="account-edit" />}
              right={(props) => (
                <IconButton
                  {...props}
                  icon="pencil-box-outline"
                  onPress={() => navigation.navigate("EditProfile")}
                />
              )}
            />
          </List.Section>
        </View>
        {Platform.OS !== "web" && (
          <List.Section>
            <List.Subheader>{t("App Updates")}</List.Subheader>
            <UpdateNotification showAlways={true} />
          </List.Section>
        )}

        {role === "SuperAdmin" && (
          <List.Section>
            <List.Subheader>{t("Administration")}</List.Subheader>
            <List.Item
              title={t("Configuration Management")}
              description={t("Manage app configurations")}
              left={(props) => <List.Icon {...props} icon="cog" />}
              onPress={() => navigation.navigate("ConfigManagement")}
            />
          </List.Section>
        )}

        <LogoutButton style={{ marginTop: "auto" }} />
        <Pressable
          testID="delete-user"
          style={{ paddingVertical: 10 }}
          onPress={deleteUser}
        >
          <ReactText>{""}</ReactText>
        </Pressable>
        <SafeAreaView />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "transparent",
  },
  contentContainer: {
    maxWidth: 600,
    width: "100%",
    alignSelf: "center",
    padding: 15,
    paddingTop: 0,
    height: "100%",
  },
  margins: {
    flexDirection: "row",
  },
  languageContainer: {
    paddingLeft: 16,
    paddingBottom: 8,
  },
  radioItem: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 4,
  },
});
